import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

/** Which section of the help widget is showing. */
export type HelpTab = "help" | "message";

type Ctx = {
  open: boolean;
  tab: HelpTab;
  /** Preselected topic for the Message-us form (e.g. "Feature request"). */
  messageCategory: string;
  /** Open the widget, optionally on a specific tab and (for Message us) a preset category. */
  openWidget: (tab?: HelpTab, category?: string) => void;
  setTab: (tab: HelpTab) => void;
  setMessageCategory: (category: string) => void;
  close: () => void;
};

const HelpWidgetCtx = createContext<Ctx>({
  open: false,
  tab: "help",
  messageCategory: "Question",
  openWidget: () => {},
  setTab: () => {},
  setMessageCategory: () => {},
  close: () => {},
});

/**
 * Provider for the Frame.io-style Help & Support widget. Mirrors
 * SearchOverlayProvider: it must sit ABOVE AppShell at the layout level so the
 * rail's Help / Feature-request buttons AND the floating widget host all share
 * the same state.
 */
export function HelpWidgetProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<HelpTab>("help");
  const [messageCategory, setMessageCategory] = useState<string>("Question");

  const openWidget = useCallback((t?: HelpTab, category?: string) => {
    if (t) setTab(t);
    if (category) setMessageCategory(category);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <HelpWidgetCtx.Provider
      value={{ open, tab, messageCategory, openWidget, setTab, setMessageCategory, close }}
    >
      {children}
    </HelpWidgetCtx.Provider>
  );
}

/** Any component under a HelpWidgetProvider can open/drive the widget. */
export function useHelpWidget() {
  return useContext(HelpWidgetCtx);
}
